import React, { RefObject, useEffect, useState } from "react";
import { Card } from "./GanttCard";
import { n } from "./ganttTypes";
import type { GanttInputs, FullResult } from "./ganttTypes";
import { CHART } from "./chartUtils";
import { flowSweepStats, optChartHit } from "./optChart";
import { flowNum, fmtFlow, fmtMassT, goalLabel, tUnit } from "./ganttUnits";
import { useSettings } from "@/hooks/useSettings";
import type { UnitSystem } from "@/lib/units";
import type { OptHover } from "./optChart";

interface OptimiserCardProps {
  inputs: GanttInputs;
  result: FullResult | null;
  optMode: "crews" | "flow";
  setOptMode: (mode: "crews" | "flow") => void;
  optCanvasRef: RefObject<HTMLCanvasElement>;
}

const batches = (b: number) => `${b} batch${b === 1 ? "" : "es"}`;

/**
 * vs Flow note: throughput now, what happens above the peak (throughput is not
 * always monotonic in flow) and, with a target, where the sweep first meets it.
 */
function flowNote(result: FullResult, goalT: number, currentFlow: number, sys: UnitSystem): string | null {
  const s = flowSweepStats(result.flow_sweep, goalT, currentFlow, result.main.mass_kg);
  if (!s) return null;
  const kgh = (f: number) => fmtFlow(f, sys);
  const fmtT = (kg: number) => fmtMassT(kg, sys);
  const optGoalLabel = (t: number) => goalLabel(t, sys);
  const range = `from ${flowNum(s.minFlow, sys)} to ${kgh(s.maxFlow)}`;
  const out = [`At ${kgh(currentFlow)}: ${fmtT(s.currentMassKg)}.`];

  const peak = `${fmtT(s.peak.mass_kg)} at ${kgh(s.peak.m_flow_kg_h)}`;
  const low = s.lowAfterPeak;
  if (s.peak.mass_kg <= 0) {
    out.push(`Nothing is frozen anywhere ${range}.`);
  } else if (s.afterPeak === "last") {
    out.push(`Highest at the top of the range: ${peak} — more flow may add more.`);
  } else if (s.afterPeak === "falls" && low) {
    out.push(`Peak: ${peak}; above that, output falls to ${fmtT(low.mass_kg)}.`);
  } else {
    out.push(`Peak: ${peak}; above that, it stays at ${fmtT(s.peak.mass_kg)}, so extra flow only builds the buffer.`);
  }

  if (goalT > 0) {
    const goal = optGoalLabel(goalT);
    const first = s.firstAtGoal;
    if (!first) {
      out.push(`Doesn't reach ${goal} anywhere ${range}.`);
    } else if (first === result.flow_sweep[0]) {
      // Met at the lowest flow swept: the real threshold is lower still.
      out.push(`Reaches ${goal} even at ${kgh(first.m_flow_kg_h)}, the lowest flow swept.`);
    } else {
      const every = s.step > 0 ? ` (points every ${kgh(s.step)})` : "";
      out.push(`Reaches ${goal} from about ${kgh(first.m_flow_kg_h)}${every}.`);
    }
  }
  return out.join(" ");
}

// Rough tooltip width, only used to decide when to flip it left of the anchor.
const TIP_W = 150;

/** Crosshair, dot and tooltip for the hovered point or bar, over the canvas (CSS px). */
function OptReadout({ h, goalT, sys }: { h: OptHover; goalT: number; sys: UnitSystem }) {
  const fmtT = (kg: number) => fmtMassT(kg, sys);
  const tipH = 18 + (h.mode === "flow" ? 2 : 3) * 17;   // rough, to keep it inside the chart
  const top = Math.max(0, Math.min(h.y - 12, h.height - tipH));
  // Keep it inside the card (its body clips): flip left of the anchor near the right edge.
  const pos: React.CSSProperties = h.x + h.gap + TIP_W > h.width
    ? { right: h.width - h.x + h.gap, top }
    : { left: h.x + h.gap, top };

  if (h.mode === "flow") {
    return (
      <>
        <div className="chart-xhair" style={{ left: h.x, top: h.top, height: h.bottom - h.top }} />
        <div className="chart-dot" style={{ left: h.x, top: h.y, background: CHART.flow }} />
        <div className="chart-tip" style={pos}>
          <div className="tip-hd">{fmtFlow(h.row.m_flow_kg_h, sys)}</div>
          <div><i style={{ background: CHART.flow }} />{fmtT(h.row.mass_kg)} · {batches(h.row.batches)}</div>
        </div>
      </>
    );
  }
  const short = goalT > 0 && h.row.mass_kg / 1000 < goalT - 1e-9;
  return (
    <div className="chart-tip" style={pos}>
      <div className="tip-hd">{h.row.n_crews} crew{h.row.n_crews === 1 ? "" : "s"}</div>
      <div><i style={{ background: short ? CHART.barDim : CHART.flow }} />{fmtT(h.row.mass_kg)} · {batches(h.row.batches)}</div>
      <div>{Math.round(h.row.crew_util * 100)}% crew utilisation</div>
    </div>
  );
}

/** Optimiser — throughput vs crews / vs raw flow (canvas drawn by drawOptChart). */
export function OptimiserCard({
  inputs, result, optMode, setOptMode, optCanvasRef,
}: OptimiserCardProps) {
  const { uiUnits: sys } = useSettings();
  const optGoalLabel = (t: number) => goalLabel(t, sys);
  const goalT = n(inputs.goalT);
  const hasGoal = goalT > 0;

  // Hover read-out: DOM overlays over the canvas, so the chart is never redrawn on hover.
  const [hover, setHover] = useState<OptHover | null>(null);
  // New results, a tab switch or a units change redraw the chart and move its geometry: drop the read-out.
  useEffect(() => { setHover(null); }, [result, optMode, sys]);

  const onMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const cv = e.currentTarget;
    const hit = optChartHit(cv, e.clientX - cv.getBoundingClientRect().left);
    // Only re-render when the hovered point / bar actually changes.
    setHover(prev => (prev && hit && prev.row === hit.row && prev.x === hit.x && prev.y === hit.y ? prev : hit));
  };

  const optimiserNote = !result?.main ? null
    : optMode === "flow" ? flowNote(result, goalT, n(inputs.mFlow, 1600), sys)
    : hasGoal
      ? (() => {
          const solve = result.crew_sweep.find(row => row.mass_kg / 1000 >= n(inputs.goalT) - 1e-9);
          // SI: the target exactly as typed (unchanged); US: converted.
          const goalTxt = sys === "US" ? optGoalLabel(goalT) : `${inputs.goalT} t`;
          return solve
            ? `${solve.n_crews} crew${solve.n_crews > 1 ? "s reach" : " reaches"} ${goalTxt} at the current flow.`
            : `Target ${goalTxt} unreachable at any crew count for this flow.`;
        })()
      : null;

  return (
    <Card
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flex: 1 }}>
          <span>Optimiser — throughput vs resource</span>
          <span style={{ display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
            <button
              data-testid="gantt-opt-crews"
              className={`tabbtn${optMode === "crews" ? " on" : ""}`}
              onClick={() => setOptMode("crews")}
            >vs Crews</button>
            <button
              data-testid="gantt-opt-flow"
              className={`tabbtn${optMode === "flow" ? " on" : ""}`}
              onClick={() => setOptMode("flow")}
            >vs Flow</button>
          </span>
        </div>
      }
    >
      <div className="chart-wrap">
        <canvas
          ref={optCanvasRef}
          data-testid="gantt-opt-chart"
          style={{ width: "100%", display: "block", height: 260 }}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        />
        {hover && <OptReadout h={hover} goalT={goalT} sys={sys} />}
      </div>
      <div className="legend" style={{ marginTop: 8 }}>
        {optMode === "crews" ? (
          <>
            <div><i style={{ background: CHART.flow }} />{tUnit(sys)}/shift vs crews</div>
            <div><i style={{ background: CHART.flow, border: `2px solid ${CHART.now}` }} />Current</div>
            <div><i style={{ background: CHART.peak }} />Recommended</div>
            {hasGoal && <div><i style={{ background: CHART.goal }} />Target {optGoalLabel(goalT)}</div>}
            <div>% on bars = crew utilisation</div>
          </>
        ) : (
          <>
            <div><i style={{ background: CHART.flow }} />{tUnit(sys)}/shift vs flow</div>
            <div><i style={{ background: CHART.now }} />Now</div>
            <div><i style={{ background: CHART.peak }} />Peak</div>
            {hasGoal && <div><i style={{ background: CHART.goal }} />Target {optGoalLabel(goalT)}</div>}
          </>
        )}
      </div>
      {optimiserNote && <p className="note" data-testid="gantt-optimiser-note" style={{ marginTop: 8 }}>{optimiserNote}</p>}
    </Card>
  );
}
